import { useCallback, useEffect } from 'react';
import {
  Control,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserNameAvailabilityQuery } from '../../../../api/user-name-availability';
import { buildUserName } from '../../../../utils/username';
import { UserFormValues } from '../user-schema';

export type UserNameStatus =
  | { kind: 'none' }
  // A name the admin typed is taken.
  | { kind: 'taken'; suggestion: string | null }
  // The generated name is taken and was swapped for the suggestion.
  | { kind: 'suggested'; takenUserName: string; suggestion: string | null };

interface UseUserNameAutoFillOptions {
  control: Control<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  setValue: UseFormSetValue<UserFormValues>;
  clearErrors: UseFormClearErrors<UserFormValues>;
  isUserNameManual: boolean;
  onUserNameManualChange: (isManual: boolean) => void;
}

function isGeneratedUserName(userName: string, autoUserName: string) {
  return (
    !!autoUserName &&
    userName.startsWith(autoUserName) &&
    /^\d*$/.test(userName.slice(autoUserName.length))
  );
}

export function useUserNameAutoFill({
  control,
  getValues,
  setValue,
  clearErrors,
  isUserNameManual,
  onUserNameManualChange,
}: UseUserNameAutoFillOptions) {
  const queryClient = useQueryClient();
  const [givenName, familyName, domain, userName] = useWatch({
    control,
    name: ['name.givenName', 'name.familyName', 'domain', 'userName'],
  });
  const { userName: autoUserName, isComplete: hasFullName } = buildUserName(
    givenName ?? '',
    familyName ?? ''
  );

  const applyUserName = useCallback(
    (value: string) => {
      setValue('userName', value, { shouldDirty: true });
      clearErrors('userName');
    },
    [setValue, clearErrors]
  );

  // Regenerates the username from the name being typed, replacing any manual
  // edit. Skipped when the change doesn't affect it (e.g. a trailing space).
  function regenerateUserName(givenNameValue: string, familyNameValue: string) {
    const next = buildUserName(givenNameValue, familyNameValue).userName;
    if (next === autoUserName) return;
    applyUserName(next);
    onUserNameManualChange(false);
  }

  const currentUserName = userName ?? '';
  const isAutoValue =
    !isUserNameManual && isGeneratedUserName(currentUserName, autoUserName);

  const nameToCheck = (
    isAutoValue ? (hasFullName ? autoUserName : '') : currentUserName
  ).toLowerCase();
  const { data: availability } = useQuery({
    ...getUserNameAvailabilityQuery(nameToCheck, domain ?? ''),
    enabled: false,
  });

  // Swaps a taken generated name for the first free one (first.last1, ...).
  useEffect(() => {
    if (!isAutoValue || !availability?.suggestion || !domain) return;
    const { suggestion } = availability;
    if (getValues('userName') === suggestion) return;

    queryClient.setQueryData(
      getUserNameAvailabilityQuery(suggestion, domain).queryKey,
      { available: true, suggestion }
    );
    applyUserName(suggestion);
  }, [
    availability,
    isAutoValue,
    domain,
    queryClient,
    getValues,
    applyUserName,
  ]);

  function getStatus(): UserNameStatus {
    if (availability && !availability.available) {
      return isAutoValue
        ? {
            kind: 'suggested',
            takenUserName: autoUserName,
            suggestion: availability.suggestion,
          }
        : { kind: 'taken', suggestion: availability.suggestion };
    }
    return { kind: 'none' };
  }

  return {
    status: getStatus(),
    applyUserName,
    onGivenNameChange: (value: string) =>
      regenerateUserName(value, familyName ?? ''),
    onFamilyNameChange: (value: string) =>
      regenerateUserName(givenName ?? '', value),
  };
}
